import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { OFFER_TYPES, UNIT_STATUSES, cycleSuffix, diffDays, type RentCycle, type UnitStatus, termSuffix, toTerm } from "@rentorng/core";
import { api } from "@/lib/api";
import { ExportButton } from "@/components/export-button";
import { titleCase, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { useLimit, useRecordLock } from "@/lib/gating";
import { Padlock, PadlockButton } from "@/components/padlock";
import { PropertyTypeMarks } from "@/components/property-icons";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { useListParams, type PageMeta } from "@/lib/list-params";
import { UnitStatusBadge } from "@/components/status";
import { Card, EmptyState, ErrorState, LinkButton, PageHeader, Spinner, StatTile, Table, Tabs } from "@/components/ui";
import { UnitGrid, ViewSwitch, type UnitCardRow } from "@/components/unit-card";
import { OfferIcon, OfferTags } from "@/components/offer-icons";

interface Totals {
  total: number;
  occupied: number;
  notice: number;
  vacant: number;
  listed: number;
  contractedRent: number;
}

interface Row extends UnitCardRow {}

export function UnitsPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const propertyId = params.get("propertyId") ?? "";
  const occupancy = params.get("occupancy") ?? "";
  const { vocab, can } = useSession();
  const fmt = useFormat();

  const list = useListParams();
  const offer = params.get("offer") ?? "";

  /*
    The padlocks, from the server. See the note on the properties list — the same two questions,
    the same answers, and the same rule that a role which could not edit a unit anyway is shown no
    padlock at all. The grid draws its own; `UnitCard` asks for itself so both screens that use it
    agree. This is here for the table, and for the way in to adding another.
  */
  const lock = useRecordLock("units");
  const ceiling = useLimit("units");
  const addGate = can("property.edit") ? (ceiling?.gate ?? null) : null;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["units", status, offer, propertyId, occupancy, list.q, list.page],
    queryFn: () =>
      api.get<{ units: Row[]; today: string; page: PageMeta; totals: Totals }>("/units", {
        status,
        offer,
        propertyId,
        occupancy,
        q: list.q,
        page: list.page,
      }),
    placeholderData: (previous) => previous,
  });

  const rows = data?.units ?? [];
  const today = data?.today ?? "";
  const totals = data?.totals;

  /**
   * Grid or list, kept in the address.
   *
   * Grid by default: every card leads with the unit's own photograph, or with the drawing of the
   * kind of building it is, and that is a faster read than a row of seven columns. The table is
   * still a click away for the times the question is about the numbers rather than the places.
   */
  const view = params.get("view") === "list" ? "list" : "grid";
  const setView = (next: "grid" | "list") => {
    const then = new URLSearchParams(params);
    if (next === "grid") then.delete("view");
    else then.set("view", next);
    setParams(then, { replace: true });
  };

  /**
   * Two filters that answer different questions.
   *
   * Status is where the unit is — occupied, vacant, being renovated. Offer is what the agency
   * wants to do with it, which is what somebody searching a listing cares about. An occupied flat
   * can be for sale, so neither filter can be folded into the other.
   */
  const tabs = [
    { to: "/app/units", label: "All" },
    ...UNIT_STATUSES.map((entry) => ({ to: `/app/units?status=${entry.value}`, label: entry.label })),
  ];
  const current = status ? `/app/units?status=${status}` : "/app/units";
  const withOffer = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set("offer", value);
    else next.delete("offer");
    next.delete("page");
    return `/app/units?${next.toString()}`;
  };

  return (
    <div>
      <PageHeader
        title={titleCase(vocab.units)}
        subtitle={`${data?.page?.total ?? rows.length} ${(data?.page?.total ?? rows.length) === 1 ? vocab.unit : vocab.units}${status ? ` · ${status.replace(/_/g, " ")}` : ""}`}
        description={`Every lettable ${vocab.unit} across the portfolio, whoever owns it. Filter by status, open one to edit its rent and its ${vocab.landlord}, or add a run of them at once.`}
        actions={
          <>
            <ExportButton kind="units" />
            {addGate ? (
              <PadlockButton gate={addGate}>Add {vocab.units}</PadlockButton>
            ) : can("property.edit") ? (
              <LinkButton to="/app/units/new" need="property.edit">Add {vocab.units}</LinkButton>
            ) : null}
          </>
        }
      />

      {/*
        What the portfolio looks like, above what is in it.

        Counted across everything the filters match rather than the page on screen, and each tile
        is the filter it describes — the figure and the way to see what is behind it are the same
        object, which is the only way a number on a screen stays trustworthy.
      */}
      {totals ? (
        <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatTile
            label="Occupied"
            value={totals.occupied + totals.notice}
            sub={totals.notice ? `${totals.notice} on notice` : `of ${totals.total}`}
            tone="success"
            to="/app/units?occupancy=occupied"
          />
          <StatTile
            label="Vacant"
            value={totals.vacant}
            sub={totals.total ? `${Math.round((totals.vacant / totals.total) * 100)}% of the portfolio` : undefined}
            tone={totals.vacant ? "warn" : "muted"}
            to="/app/units?status=vacant"
          />
          <StatTile
            label="On the public site"
            value={totals.listed}
            sub={`of ${totals.total} ${totals.total === 1 ? vocab.unit : vocab.units}`}
            tone="brand"
          />
          <StatTile
            label="Contracted rent"
            value={fmt.money(totals.contractedRent)}
            sub="across live tenancies"
            tone="brand"
          />
        </div>
      ) : null}

      <Tabs tabs={tabs} current={current} />

      {/* What it is on the market as, which is a different question from where it is. */}
      <div className="mb-4 flex flex-wrap gap-2">
        <Link
          to={withOffer("")}
          aria-current={offer ? undefined : "true"}
          className={`inline-flex items-center gap-2 rounded-[var(--radius-control)] px-3.5 py-2 text-sm font-medium transition-colors ${
            offer ? "bg-ink-100 text-ink-600 hover:bg-ink-200" : "bg-brand-600 text-white"
          }`}
        >
          Any offer
        </Link>
        {OFFER_TYPES.map((entry) => (
          <Link
            key={entry.value}
            to={withOffer(entry.value)}
            aria-current={offer === entry.value ? "true" : undefined}
            className={`inline-flex items-center gap-2 rounded-[var(--radius-control)] px-3.5 py-2 text-sm font-medium transition-colors ${
              offer === entry.value ? "bg-brand-600 text-white" : "bg-ink-100 text-ink-600 hover:bg-ink-200"
            }`}
          >
            <OfferIcon offer={entry.value} size={17} />
            {entry.short}
          </Link>
        ))}
      </div>

      <Card padded={false}>
        <div className="flex flex-wrap items-center gap-3 border-b border-ink-200 p-3">
          <div className="min-w-0 flex-1">
            <SearchBox params={list} placeholder={`Search ${vocab.units} or ${vocab.property}…`} />
          </div>
          <ViewSwitch view={view} onChange={setView} />
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={status ? `No ${vocab.units} with that status.` : `No ${vocab.units} yet.`}
            hint={`Add a ${vocab.property} first, then its ${vocab.units}.`}
            action={
              status ? (
                <button className="btn btn-secondary" onClick={() => setParams({})}>Show all</button>
              ) : addGate ? (
                <PadlockButton gate={addGate}>Add {vocab.units}</PadlockButton>
              ) : can("property.edit") ? (
                <LinkButton to="/app/units/new" need="property.edit">Add {vocab.units}</LinkButton>
              ) : undefined
            }
          />
        ) : view === "grid" ? (
          <div className="p-4">
            <UnitGrid units={rows} today={today} />
          </div>
        ) : (
          <Table
            head={
              <tr>
                <th>{titleCase(vocab.unit)}</th>
                <th>{titleCase(vocab.property)}</th>
                <th>{titleCase(vocab.landlord)}</th>
                <th>Status</th>
                <th>{titleCase(vocab.tenant)}</th>
                <th>Ends</th>
                <th className="num">{titleCase(vocab.rent)}</th>
                <th className="num">Empty for</th>
              </tr>
            }
          >
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <span className="flex flex-wrap items-center gap-2">
                    <Link
                      to={can("property.edit") ? `/app/units/${row.id}/edit` : `/app/properties/${row.propertyId}`}
                      className="link"
                    >
                      {row.label}
                    </Link>
                    {/* The same mark the card carries, in the column that names the unit. */}
                    {lock.isLocked(row.id) ? <Padlock gate={lock.gate} label="Locked" /> : null}
                    {row.bedrooms ? <span className="text-xs text-ink-400">{row.bedrooms} bed</span> : null}
                    {/* Only worth showing when it is not the ordinary case: every unit being
                        "to rent" would put the same chip on every row and say nothing. */}
                    {row.offers && !(row.offers.length === 1 && row.offers[0] === "rent") ? (
                      <OfferTags offers={row.offers} />
                    ) : null}
                  </span>
                </td>
                <td>
                  <span className="flex items-center gap-2">
                    <PropertyTypeMarks types={row.propertyTypes} size={15} max={2} />
                    <Link to={`/app/properties/${row.propertyId}`} className="text-ink-600 hover:underline">{row.propertyName}</Link>
                  </span>
                </td>
                {/* Whose unit this is. A block let by one agency can have a different owner on
                    every floor, and the column is the only place that is visible at a glance. */}
                <td>
                  {row.ownerId ? (
                    <Link
                      to={`/app/clients/${row.ownerId}`}
                      className={row.ownerIsOwnLandlord ? "link font-medium" : "text-ink-600 hover:underline"}
                      title={row.ownerIsOwnLandlord ? `Owned separately from the ${vocab.property}` : undefined}
                    >
                      {row.ownerName}
                    </Link>
                  ) : (
                    <span className="text-ink-400">—</span>
                  )}
                </td>
                <td><UnitStatusBadge status={row.status as UnitStatus} /></td>
                <td className="text-ink-600">{row.tenantName ?? <span className="text-ink-400">—</span>}</td>
                <td className="text-ink-500">
                  {row.leaseId ? <Link to={`/app/tenancies/${row.leaseId}`} className="hover:underline">{fmt.date(row.leaseEnd)}</Link> : "—"}
                </td>
                <td className="num tabular">
                  {row.rentAmount ? (
                    <>
                      {fmt.money(row.rentAmount)}
                      <span className="block text-xs text-ink-400">{termSuffix(toTerm(row.rentCycle as RentCycle, row.rentCycleEvery))}</span>
                    </>
                  ) : "—"}
                </td>
                <td className="num tabular">
                  {row.status === "vacant" && row.vacantSince && today ? (
                    <span className="text-danger-700">{diffDays(row.vacantSince, today)} days</span>
                  ) : (
                    <span className="text-ink-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}

        <Pagination meta={data?.page} params={list} noun={vocab.units} />
      </Card>
    </div>
  );
}
